/**
 * <Stack> — vertical flex container with locked gap scale.
 *
 * Replaces ad-hoc `flex flex-col gap-N` patterns scattered across pages.
 * Pairs with <Cluster> (horizontal) and <Grid> (2D).
 *
 * Spec: docs/design-system/03-spacing-layout.md
 */

import { forwardRef, type HTMLAttributes, type ElementType, type ReactNode } from 'react';
import { cn } from '../utils/cn';

export type StackGap = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface StackProps extends HTMLAttributes<HTMLDivElement> {
  /** Vertical gap between children. Maps to spacing scale. Default `md` (16px). */
  gap?: StackGap;
  /** Cross-axis alignment. */
  align?: 'start' | 'center' | 'end' | 'stretch';
  /** Render an alternate tag — e.g. `'section'`, `'ul'`. Default `'div'`. */
  as?: ElementType;
  children: ReactNode;
}

const GAP_CLASSES: Record<StackGap, string> = {
  none: 'gap-0',
  xs: 'gap-1',   // 4px
  sm: 'gap-2',   // 8px
  md: 'gap-4',   // 16px — default
  lg: 'gap-6',   // 24px
  xl: 'gap-8',   // 32px
  '2xl': 'gap-12', // 48px
};

const ALIGN_CLASSES: Record<NonNullable<StackProps['align']>, string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

export const Stack = forwardRef<HTMLDivElement, StackProps>(function Stack(
  { gap = 'md', align, as: Tag = 'div', className, children, ...rest },
  ref,
) {
  return (
    <Tag
      ref={ref as never}
      className={cn('flex flex-col', GAP_CLASSES[gap], align && ALIGN_CLASSES[align], className)}
      {...rest}
    >
      {children}
    </Tag>
  );
});

Stack.displayName = 'Stack';

export default Stack;
